import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdCheckboxGroup,
  CmdChipSelect,
  CmdInput,
  CmdLabel,
  CmdSimpleSelect,
  CmdSimpleSelectOption,
  CmdSubpage,
  CmdSubpageButton,
  CmdSuperSelect,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { AxiosResponse } from 'axios';
import { IValidationApiResponse } from '../../Services/FormikValidatorService';
import ConfirmDialog from '../ConfirmDialog';
import * as EmployeeService from './EmployeeService';
import {
  getHours,
  getMinutes,
  getTime,
  handleChipOptionsCategory,
  IS_ACTIVE,
  IS_DRIVER,
  IS_PENDING,
  IS_USER,
  isDriverAllowed,
  isVehicleAllowed,
} from './EmployeeService';
import usePageWithForm from '../../hooks/usePageWithForm';
import OnlineHelp from '../OnlineHelp';
import Calendar from '../Forms/ReactHookFormFields/Calendar';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import Input from '../Forms/ReactHookFormFields/Input';
import { getCustomerId } from '../../Services/UserService';
import {
  IEmployeeData,
  IEmployeeFormError,
  IEmployeeInputForm,
  IEmployeeValidation,
  IRoleOption,
} from './types';
import PermissionsService, { PERMISSION_PERSON } from '../../Services/PermissionsService';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';
import showMessage from '../Toastr/ToastService';

interface FormProps {
  isEdited: boolean;
  closeCallback: () => void;
  editId: number | null;
  updateTable?: (() => void) | undefined;
}

export interface IAdditionalValues {
  customerId?: string | number | null;
  isUser?: boolean;
  isDriver?: boolean;
  id?: number | null;
  username?: string;
  allowedVehiclesList?: boolean;
  allowedDriversList?: boolean;
}

function Form(props: FormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [isCancelButtonClicked, setIsCancelButtonClicked] = React.useState<boolean>(false);
  const [lastUsername, setLastUsername] = React.useState('');
  const [isWorkDepatmentEnabled, setIsWorkDepartmentEnabled] = React.useState<boolean>(false);
  const [isCostCenterEnabled, setIsCostCenterEnabled] = React.useState<boolean>(false);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const [driversOptions, setDriversOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [vehiclesOptions, setVehiclesOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [rolesOptions, setRolesOptions] = React.useState<IRoleOption[]>([]);
  const [validation, setValidation] = React.useState<IValidationApiResponse>({});
  const [employeeValidation, setEmployeeValidation] = React.useState<IEmployeeValidation>({
    isDefaultDriver: false,
    isOnRide: false,
  });
  const [defaultDriverVehicles, setdefaultDriverVehicles] = React.useState<string[]>([]);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [customerOptions, setCustomerOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [twoFactorAuthEnabled, setTwoFactorAuthEnabled] = React.useState<boolean>(false);
  const [workDepartmentOptions, setWorkDepartmentOptions] = React.useState<CmdSimpleSelectOption[]>(
    []
  );
  const [costCenterOptions, setCostCenterOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [idCardOptions, setIdCardOptions] = React.useState<CmdSimpleSelectOption[]>([]);
  const [userExists, setUserExists] = React.useState<boolean>(false);
  const [origUsername, setOrigUsername] = React.useState('');
  const [origName, setOrigName] = React.useState('');
  const [origLastName, setOrigLastName] = React.useState('');
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    trigger,
    setError,
    watch,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<IEmployeeInputForm>({
    mode: 'onChange',
    defaultValues: {
      customerId: null,
      title: '',
      firstName: '',
      lastName: '',
      identCards: null,
      personalNumber: '',
      workDepartmentId: null,
      costCenterId: null,
      workingHoursFrom: new Date(new Date().setHours(8, 0, 0, 0)),
      workingHoursTo: new Date(new Date().setHours(16, 0, 0, 0)),
      email: '',
      phone: '',
      addressStreet: '',
      addressCity: '',
      addressPostal: '',
      types: [],
      roleIsRequired: false,
      status: '',
    },
    resolver: async (data) => {
      let formValidation = { ...validation };

      if (!data.customerId) {
        formValidation = {
          ...formValidation,
          roles: [],
          vehicles: [],
          drivers: [],
        };
      }

      const validateErrors = validateFields(data, formValidation);
      let formErrors = transformErrors(validateErrors);

      if (formValidation && formValidation.workingHoursFrom) {
        const workingHoursFrom = new Date(data.workingHoursFrom);
        const maxDateValidation = formValidation.workingHoursFrom.find(
          (item) => item.param === 'maxDate'
        );

        if (maxDateValidation && workingHoursFrom > new Date()) {
          formErrors = {
            ...formErrors,
            workingHoursFrom: { type: 'maxDate', message: maxDateValidation.message },
          };
        }
      }

      if (data.types.includes(IS_USER) && !data.username) {
        formErrors = {
          ...formErrors,
          username: { message: f({ id: 'form.required' }) },
        };
      }

      if ((origUsername === lastUsername || !lastUsername) && props.editId) {
        clearErrors('username');
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data,
        errors: formErrors,
      };
    },
  });

  const driverTooltip = (): string => {
    const vehicles = defaultDriverVehicles?.join(', ');
    if (employeeValidation && props.editId) {
      if (employeeValidation.isOnRide && watch('types').includes(IS_DRIVER)) {
        return f({ id: 'employee.validation.isOnRide' }, { value: vehicles });
      }
      if (employeeValidation.isDefaultDriver && watch('types').includes(IS_DRIVER)) {
        return f({ id: 'employee.validation.isDefaultDriver' }, { value: vehicles });
      }
    }
    return '';
  };

  const userTooltip = (): string => {
    if (props.isEdited && (watch('status') === IS_ACTIVE || watch('status') === IS_PENDING)) {
      return f({ id: 'employee.field.isUser.active.tooltip' });
    }
    return '';
  };

  const driverDisabled = () => {
    if (employeeValidation && props.editId) {
      if (employeeValidation.isOnRide && watch('types').includes(IS_DRIVER)) {
        return true;
      }
      if (employeeValidation.isDefaultDriver && watch('types').includes(IS_DRIVER)) {
        return true;
      }
    }
    return false;
  };

  const getCustomerLabelValue = () => {
    if (props.editId) {
      return customers.find((customer) => customer.id === watch('customerId'))?.name;
    }
    return customerOptions[0]?.item;
  };

  const getTwoFactorAuthTooltip = () => {
    if (!twoFactorAuthEnabled) {
      return f({ id: 'employee.2fa.tooltip' });
    }
    if (!watch('phone')) {
      return f({ id: 'employee.twoFactorAuth.tooltip' });
    }
    return '';
  };

  const checkUserExists = async (): Promise<boolean> => {
    const customerId = watch('customerId');
    const firstName = watch('firstName');
    const lastName = watch('lastName');

    const checkAndReturnUserExists = async (id: number) => {
      const response = await EmployeeService.userExists(firstName, lastName, id);
      return !!response;
    };

    if (watch('customerId') && watch('firstName') && watch('lastName')) {
      return checkAndReturnUserExists(Number(customerId));
    }
    if (customerOptions.length === 1) {
      return checkAndReturnUserExists(Number(customerOptions[0].value));
    }

    return false;
  };

  const getIdentificationCards = async (customerId: number) => {
    const idCardsResponse = await EmployeeService.getIdentificationCards(customerId, props.editId);
    if (idCardsResponse) {
      setIdCardOptions(idCardsResponse);
    }
  };

  const getCostCenters = async (customerId: number) => {
    const costCentersResponse = await EmployeeService.getCostCenters(customerId);
    if (costCentersResponse) {
      setCostCenterOptions(costCentersResponse);
      setIsCostCenterEnabled(true);
    }
  };

  const getWorkDepartments = async (customerId: number) => {
    const workDepartmentsResponse = await EmployeeService.getWorkDepartments(customerId);
    if (workDepartmentsResponse) {
      setWorkDepartmentOptions(workDepartmentsResponse);
      setIsWorkDepartmentEnabled(true);
    }
  };

  const loadCustomers = async () => {
    const customersResponse = await EmployeeService.getCustomers();
    if (customersResponse) {
      const filteredCustomers = customersResponse.filter((i) =>
        PermissionsService.hasAccess(PERMISSION_PERSON, ['create'], Number(i.value))
      );

      setCustomerOptions(
        filteredCustomers.map((i) => {
          i.value = String(i.value);
          return i;
        })
      );

      if (filteredCustomers.length === 1) {
        const customerValue = filteredCustomers[0]?.value;

        setValue('customerId', customerValue);
        getCostCenters(Number(customerValue));
        getWorkDepartments(Number(customerValue));
        setIsWorkDepartmentEnabled(true);
        setIsCostCenterEnabled(true);
      }
    }
  };

  const handleOnChangeCustomer = (value: string | number) => {
    if (value) {
      getCostCenters(Number(value));
      getWorkDepartments(Number(value));
      setIsWorkDepartmentEnabled(true);
      setIsCostCenterEnabled(true);
    } else {
      setIsWorkDepartmentEnabled(false);
      setIsCostCenterEnabled(false);
    }
  };

  const loadDataForForm = async () => {
    if (props.editId) {
      await loadCustomers();
      const employeeResponse = await EmployeeService.get(props.editId);
      if (employeeResponse) {
        if (!selectedCustomers.includes(employeeResponse.customerId)) {
          props.closeCallback();
        }

        await getIdentificationCards(employeeResponse.customerId);
        await getCostCenters(employeeResponse.customerId);
        await getWorkDepartments(employeeResponse.customerId);
        const workingHours = employeeResponse.workingHours
          ? employeeResponse.workingHours.split('-')
          : '';
        const types: string[] = [];
        if (employeeResponse.isUser) {
          types.push(IS_USER);
        }
        if (employeeResponse.isDriver) {
          types.push(IS_DRIVER);
        }
        const assignedIdentCardsValues = employeeResponse.assignedIdentCards
          ? employeeResponse.assignedIdentCards.map((i: string | number) => String(i))
          : [];

        // Nastavenie hodnôt do react-hook-form pomocou setValue
        setValue('customerId', employeeResponse.customerId);
        setValue('title', employeeResponse.title || '');
        setValue('firstName', employeeResponse.firstName || '');
        setValue('lastName', employeeResponse.lastName || '');
        setValue('identCards', assignedIdentCardsValues);
        setValue('personalNumber', employeeResponse.personalNumber || '');
        setValue(
          'workDepartmentId',
          employeeResponse.workDepartmentId ? String(employeeResponse.workDepartmentId) : ''
        );
        setValue(
          'costCenterId',
          employeeResponse.costCenterId ? String(employeeResponse.costCenterId) : ''
        );
        setValue('workingHoursFrom', getTime(workingHours[0]));
        setValue('workingHoursTo', getTime(workingHours[1]));
        setValue('email', employeeResponse.email || '');
        setValue('phone', employeeResponse.phone || '');
        setValue('addressStreet', employeeResponse.addressStreet || '');
        setValue('addressCity', employeeResponse.addressCity || '');
        setValue('addressPostal', employeeResponse.addressPostal || '');
        setValue('types', types);
        setLastUsername(employeeResponse.username);
        setOrigUsername(employeeResponse.username);
        setOrigName(employeeResponse.firstName);
        setOrigLastName(employeeResponse.lastName);

        let employeeVal: IEmployeeValidation = {
          isDefaultDriver: false,
          isOnRide: false,
        };
        if (employeeResponse.validation) {
          employeeVal = {
            isDefaultDriver: employeeResponse.validation.defaultDriver,
            isOnRide: employeeResponse.validation.onRide,
          };
        }
        const workDepartmentsResponse = await EmployeeService.getWorkDepartments(
          employeeResponse.customerId
        );
        if (workDepartmentsResponse) {
          setWorkDepartmentOptions(workDepartmentsResponse);
        }
        const costCentersResponse = await EmployeeService.getCostCenters(
          employeeResponse.customerId
        );
        if (costCentersResponse) {
          setCostCenterOptions(costCentersResponse);
        }

        setEmployeeValidation(employeeVal);
        setdefaultDriverVehicles(employeeResponse.defaultDriverVehicles || []);
      }
    }
  };

  const loadRoles = async () => {
    const customerIdFromUserService = getCustomerId();

    const values = {
      customerId: watch('customerId') || customerIdFromUserService,
      username: watch('username'),
      isUser: watch('types')?.includes(IS_USER),
      isDriver: watch('types')?.includes(IS_DRIVER),
      id: props.editId ? Number(props.editId) : undefined,
    };
    const rolesResponse = await EmployeeService.getRoles(values);
    if (rolesResponse) {
      setRolesOptions(
        rolesResponse.map((i) => {
          i.value = String(i.value);
          return i;
        })
      );
      trigger();
    }
  };

  const getValidation = async () => {
    const customerId = watch('customerId');
    const username = watch('username');
    const isUser = watch('types')?.includes(IS_USER);
    const roles = rolesOptions.map((i) => i.value);

    const values = {
      customerId,
      ...(username && { username }),
      isUser,
      roles,
    };

    const validationResponse = await EmployeeService.getValidation(values);
    if (validationResponse) {
      setValidation(validationResponse);
      trigger();
    }
  };

  const loadDrivers = async () => {
    const customerIdFromUserService = getCustomerId();
    const values = {
      username: watch('username'),
      isUser: watch('types')?.includes(IS_USER),
      isDriver: watch('types')?.includes(IS_DRIVER),
      customerId: watch('customerId') || customerIdFromUserService,
      roles: watch('roles')?.map((i) => Number(i)),
      id: props.editId ? Number(props.editId) : undefined,
    };
    const driversResponse = await EmployeeService.getDrivers(values);
    if (driversResponse) {
      const tempResponse = driversResponse.map((i) => {
        i.value = String(i.value);
        return i;
      });
      setDriversOptions(tempResponse);
    }
  };

  const loadVehicles = async () => {
    const customerIdFromUserService = getCustomerId();
    const values = {
      username: watch('username'),
      isUser: watch('types')?.includes(IS_USER),
      isDriver: watch('types')?.includes(IS_DRIVER),
      customerId: watch('customerId') || customerIdFromUserService,
      roles: watch('roles')?.map((i) => Number(i)),
      id: props.editId ? Number(props.editId) : undefined,
    };
    const vehiclesResponse = await EmployeeService.getVehicles(values);
    if (vehiclesResponse) {
      const tempResponse = vehiclesResponse.map((i) => {
        i.value = String(i.value);
        return i;
      });
      setVehiclesOptions(tempResponse);
      if (props.editId && watch('vehicles') && tempResponse.length > 0) {
        setValue('vehicles', watch('vehicles'));
      }
    }
  };

  const getCustomerSettings = async () => {
    const customerId = watch('customerId');
    if (customerId) {
      const res = await EmployeeService.getCustomerSettings(String(customerId));
      setTwoFactorAuthEnabled(!!res);
    }
  };

  const handleUserData = async () => {
    if (props.editId) {
      // setIsLoading(true);
      const userResponse = await EmployeeService.user(props.editId);
      if (userResponse) {
        const twoFactorAuth: string[] = [];
        if (userResponse.user.twoFactorAuth) {
          twoFactorAuth.push('twoFactorAuthWeb');
        }
        if (userResponse.user.twoFactorAuthMobile) {
          twoFactorAuth.push('twoFactorAuthMobile');
        }
        setValue('username', userResponse.user.username);
        setLastUsername(userResponse.user.username);
        setOrigUsername(userResponse.user.username);
        setValue(
          'roles',
          userResponse.roles && (userResponse.roles.customer || userResponse.roles.salesChannel)
            ? userResponse.roles.customer.length > 0
              ? userResponse.roles.customer.map((i) => String(i))
              : userResponse.roles.salesChannel.map((i) => String(i))
            : []
        );
        setValue(
          'vehicles',
          userResponse.vehicles.map((i) => String(i))
        );
        setValue(
          'drivers',
          userResponse.drivers.map((i) => String(i))
        );
        setValue('twoFactorAuth', twoFactorAuth);
        setValue('status', userResponse.user.status);
        return userResponse;
      }
      return null;
      // setIsLoading(false);
    }
    return null;
  };

  const handleCheckboxesOnChange = (values: string[]) => {
    setValue('types', values);
    trackEvent(['employee form', 'change', 'Change type of employee']);

    if (values.includes(IS_USER) && !watch('username')) {
      setError('username', { message: f({ id: 'form.required' }) });
    } else {
      clearErrors('username');
    }

    if (!values.includes(IS_DRIVER)) {
      setValue('identCards', []);
    }
    if (values.includes(IS_DRIVER) && customerOptions.length === 1) {
      const customerValue = customerOptions[0]?.value;
      getIdentificationCards(Number(customerValue));
    } else if (values.includes(IS_DRIVER) && watch('customerId')) {
      getIdentificationCards(Number(watch('customerId')));
    }
    trigger();
  };

  const handleCancelConfirmDialog = () => {
    setIsCancelButtonClicked(false);
    trackEvent(['employee form', 'click', 'Cancel confrim employee form']);
  };

  const handleConfirmCloseSubpage = () => {
    props.closeCallback();
    trackEvent(['employee form', 'click', 'Close confirm employee form']);
  };

  const closeSubpage = () => {
    setIsCancelButtonClicked(true);
    trackEvent(['employee form', 'click', 'Close subpage employee form']);
  };

  const handleCancelUserExistConfirmDialog = () => {
    setUserExists(false);
  };

  const handleOnSubmit = async (values: IEmployeeInputForm) => {
    trackEvent(['employee form', 'click', 'Submit employee form']);
    const workingHoursFrom = `${getHours(values.workingHoursFrom)}:${getMinutes(values.workingHoursFrom)}`;
    const workingHoursTo = `${getHours(values.workingHoursTo)}:${getMinutes(values.workingHoursTo)}`;

    if (!props.editId || origName !== values.firstName || origLastName !== values.lastName) {
      const userExistResponse = await checkUserExists();

      if (userExistResponse && !userExists) {
        setUserExists(true);
        return false;
      }
    }

    const data: IEmployeeData = {
      title: values.title,
      firstName: values.firstName,
      lastName: values.lastName,
      personalNumber: values.personalNumber,
      isUser: values.types.includes(IS_USER) || false,
      isDriver: values.types.includes(IS_DRIVER) || false,
      customerId: Number(values.customerId),
      costCenterId: Number(values.costCenterId),
      workDepartmentId: Number(values.workDepartmentId),
      email: values.email,
      phone: values.phone,
      addressCity: values.addressCity,
      addressPostal: values.addressPostal,
      addressStreet: values.addressStreet,
      assignedIdentCards: values.identCards,
      workingHours: `${workingHoursFrom}-${workingHoursTo}`,
      username: values.username,
      roles: values.roles,
      drivers: values.drivers,
      vehicles: values.vehicles,
      twoFactorAuthWeb: values?.twoFactorAuth?.includes('twoFactorAuthWeb'),
      twoFactorAuthMobile: values?.twoFactorAuth?.includes('twoFactorAuthMobile'),
    };

    const handleValidationErrors = (validationErrors: Record<string, string[]>) => {
      Object.keys(validationErrors).forEach((key) => {
        const errorMessage = validationErrors[key][0];
        if (errorMessage && !errors[key]) {
          setError(key, { message: errorMessage });
        }
      });
    };

    if (props.editId) {
      const editResponse = await EmployeeService.update(props.editId, data);

      if (editResponse && (editResponse as IEmployeeFormError).errors) {
        handleValidationErrors((editResponse as IEmployeeFormError).errors);
        return false;
      }

      if (
        editResponse &&
        (editResponse as AxiosResponse)?.data &&
        (editResponse as AxiosResponse)?.status === 200
      ) {
        if (props.updateTable) {
          props.closeCallback();
          props.updateTable();
          reset();
        }
      }

      if (editResponse && (editResponse as AxiosResponse).data === null) {
        showMessage('', (editResponse as AxiosResponse)?.toast.message, 'error');
        return false;
      }

      return true;
    }

    const addResponse = await EmployeeService.save(data);

    if (addResponse && (addResponse as IEmployeeFormError).errors) {
      handleValidationErrors((addResponse as IEmployeeFormError).errors);
      return false;
    }

    if (props.updateTable) {
      props.closeCallback();
      props.updateTable();
      reset();
    }

    return true;
  };

  const onSubmit = async (values) => {
    if (Object.keys(errors).length === 0) {
      if (await handleOnSubmit(values)) {
        if (props.updateTable) {
          props.closeCallback();
          props.updateTable();
        }
      }
    } else {
      trigger();
    }
  };
  const handleSubmitlUserExist = async () => {
    setUserExists(false);
    const values = getValues();
    await handleOnSubmit(values);
  };

  const subpageButtons: CmdSubpageButton[] = [
    {
      id: 'employee-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'form.close' }),
      closeCallback: closeSubpage,
    },
    {
      id: 'employee-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'form.save' }),
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const results = useQueries({
    queries: [
      {
        queryKey: [
          'employees-form-customers',
          props.closeCallback,
          props.editId,
          loadCustomers,
          selectedCustomers,
        ],
        queryFn: () => loadCustomers(),
        enabled: !props.editId,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-data', props.editId, selectedCustomers],
        queryFn: () => loadDataForForm(),
        enabled: selectedCustomers.length > 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-validation'],
        queryFn: () => getValidation(),
        // enabled: !!(watch('username') && rolesOptions.length > 0),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-roles', lastUsername, props.editId],
        queryFn: () => loadRoles(),
        enabled:
          (!!lastUsername && watch('types')?.includes(IS_USER)) ||
          (!!lastUsername && !!props.editId && watch('types')?.includes(IS_USER)),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-drivers', watch('roles')],
        queryFn: () => loadDrivers(),
        enabled: !!(
          watch('username') &&
          isDriverAllowed(watch('roles'), rolesOptions) &&
          rolesOptions.length > 0
        ),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-vehicles', watch('roles')],
        queryFn: () => loadVehicles(),
        enabled: !!(
          watch('username') &&
          isVehicleAllowed(watch('roles'), rolesOptions) &&
          rolesOptions.length > 0
        ),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-customer-settings'],
        queryFn: () => getCustomerSettings(),
        enabled: !!watch('customerId'),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['employees-form-user', props.editId, selectedCustomers],
        queryFn: () => handleUserData(),
        enabled: !!(selectedCustomers.length > 0 && watch('types').includes(IS_USER)),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { data: customerOptionsData, isLoading: isLoadingCustomers } = results[0];
  const { data: validationData, isLoading: isLoadingDataForm } = results[1];
  const { data: validationRolesData, isLoading: isLoadingValidation } = results[2];
  const { data: rolesData, isLoading: isLoadingRoles } = results[3];
  const { data: driversData, isLoading: isLoadingDrivers } = results[4];
  const { data: vehiclesData, isLoading: isLoadingVehicles } = results[5];
  const { data: customerSettingsData, isLoading: isLoadingCustomerSettings } = results[6];
  const { data: userData, isLoading: isLoadingUserData } = results[7];

  const isLoading =
    isLoadingCustomers ||
    isLoadingDataForm ||
    isLoadingValidation ||
    isLoadingRoles ||
    isLoadingDrivers ||
    isLoadingVehicles ||
    isLoadingCustomerSettings ||
    isLoadingUserData;

  const handleDisabledRoles = () => {
    if (userData?.roles?.salesChannel && userData.roles.salesChannel.length > 0) {
      return true;
    }

    return !(watch('types') && watch('types').includes(IS_USER) && watch('username'));
  };

  const handleDisabledVehiclesAndDrivers = () => {
    if (
      props.editId &&
      Array.isArray(userData?.roles?.salesChannel) &&
      userData.roles.salesChannel.length > 0
    ) {
      return true;
    }
    // Original condition: only disable if types do not include IS_USER
    return !(watch('types') && watch('types').includes(IS_USER));
  };

  const getLabelFromOptions = (
    selectedValues: string[],
    options: CmdSimpleSelectOption[]
  ): string => {
    if (!options || selectedValues.length === 0) return '';

    const selectedItems = options
      .filter((option) => selectedValues.includes(String(option.value)))
      .map((option) => option.item);

    return selectedItems.join(', ');
  };

  // Debounced version of the username handler
  const handleDebouncedUsernameChange = React.useMemo(
    () =>
      debounce((value) => {
        loadRoles(); // Call your function here only after debounce
      }, 500),
    []
  );

  const renderSelectOrLabel = (
    isEditMode: boolean,
    salesChannelHasItems: boolean,
    labelTitle: string,
    selectOptions: CmdSimpleSelectOption[],
    selectValue: string[],
    allLabelText: string,
    isAllowed: boolean,
    optionsExist: boolean,
    controlName: 'vehicles' | 'drivers',
    isRequired: boolean,
    error
  ) => {
    if (isEditMode && salesChannelHasItems) {
      // Render CmdLabel if in edit mode and salesChannel has items

      return (
        <CmdLabel
          title={labelTitle}
          value={isAllowed ? getLabelFromOptions(selectValue, selectOptions) : allLabelText}
        />
      );
    }

    if (isAllowed && optionsExist) {
      // Render CmdSuperSelect if allowed and options exist
      return (
        <Controller
          name={controlName}
          control={control}
          rules={{ required: error ? error.message : undefined }}
          render={({ field }) => (
            <CmdSuperSelect
              {...field}
              id={controlName}
              label={labelTitle}
              placeholder={f({ id: 'employee.field.idCard.placeholder' })}
              searchPlaceholder={f({ id: 'form.search' })}
              options={handleChipOptionsCategory(selectOptions, selectValue || [], [
                f({ id: 'alarms.modal.rule.choice.select' }),
                f({ id: 'alarms.modal.rule.choice.not.select' }),
              ])}
              searchTooltip={f({ id: 'alarms.section.vehicles.vehicleId.search.tooltip' })}
              selectButtonTooltip={f({
                id: 'alarms.section.vehicles.vehicleId.selectButton.tooltip',
              })}
              resetButtonTooltip={f({
                id: 'alarms.section.vehicles.vehicleId.resetButton.tooltip',
              })}
              error={error?.message}
              value={selectValue}
              onChangeCallback={(value: string[]) => {
                setValue(controlName, value);
                trigger(controlName);
                trackEvent(['employee form', 'change', `Change ${controlName}`]);
              }}
              disabled={!(watch('types') && watch('types').includes(IS_USER))}
              setTouched={() => trigger(controlName)}
              required={isRequired}
              touched
              isArrayValue
              hasButton
              disableTooltipOnChips
              textAfterDeletedItem="(Arch.)"
            />
          )}
        />
      );
    }

    // Render fallback CmdLabel with all items message
    return <CmdLabel title={labelTitle} value={allLabelText} />;
  };

  return (
    <CmdSubpage
      id="employee-form"
      maintitle={f({ id: 'menu.settings' })}
      title={f({ id: 'menu.employeeSettings' })}
      subtitle={props.editId ? f({ id: 'employee.edit' }) : f({ id: 'employee.add' })}
      buttons={subpageButtons}
      loader={isLoading}
      onlineHelp={<OnlineHelp topic={props.editId ? 'edit-employee' : 'add-employee'} />}
    >
      <div className="row">
        <div className="col-xxl-6">
          <CmdBox
            id="employee-basic-information"
            title={f({ id: 'employee.section.basicInformation' })}
          >
            {props.editId || (customerOptions && customerOptions.length === 1) ? (
              <CmdLabel
                id="customerId"
                title={f({ id: 'employee.field.customerId' })}
                value={getCustomerLabelValue()}
              />
            ) : (
              <Controller
                name="customerId"
                control={control}
                // rules={{ required: errors.customerId ? errors.customerId.message : undefined }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    name="customerId"
                    id="customerId"
                    label={f({ id: 'employee.field.customerId' })}
                    introOption={f({ id: 'employee.field.customerId.placeholder' })}
                    options={customerOptions}
                    value={String(watch('customerId'))}
                    required={true}
                    onChangeCallback={(value: number) => {
                      setValue('customerId', value);
                      trigger('customerId');
                      handleOnChangeCustomer(value);
                      trackEvent(['employee form', 'change', 'Change customerId']);
                    }}
                    error={errors.customerId?.message}
                    touched={true}
                  />
                )}
              />
            )}
            <Controller
              name="title"
              control={control}
              rules={{ required: errors.title ? errors.title.message : undefined }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="title"
                  label={f({ id: 'employee.field.title' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('title', value);
                    trigger('title');
                  }}
                  error={errors.title?.message}
                  touched
                />
              )}
            />
            <Controller
              name="firstName"
              control={control}
              rules={{ required: errors.firstName ? errors.firstName.message : undefined }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="firstName"
                  label={f({ id: 'employee.field.firstName' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('firstName', value);
                    trigger('firstName');
                  }}
                  required
                  error={errors.firstName?.message}
                  touched
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: errors.firstName ? errors.firstName.message : undefined }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="lastName"
                  label={f({ id: 'employee.field.lastName' })}
                  type="text"
                  value={watch('lastName')}
                  onChangeCallback={(value) => {
                    setValue('lastName', value);
                    trigger('lastName');
                  }}
                  required
                  error={errors.lastName?.message}
                  touched
                  options={[
                    {
                      value: IS_USER,
                      item: f({ id: 'employee.field.isUser' }),
                      disabled: !!(
                        props.isEdited &&
                        (watch('status') === IS_ACTIVE || watch('status') === IS_PENDING)
                      ),
                      tooltip: userTooltip(),
                    },
                    {
                      value: IS_DRIVER,
                      item: f({ id: 'employee.field.isDriver' }),
                      disabled: driverDisabled(),
                      tooltip: driverTooltip(),
                    },
                  ]}
                  selectedItems={watch('types')}
                  tooltip={f({ id: 'alarms.modal.recipient.formField.alertType.tooltip' })}
                  handleCheckboxesOnChange={handleCheckboxesOnChange}
                />
              )}
            />
          </CmdBox>

          <CmdBox
            id="employee-additional-information"
            title={f({ id: 'employee.section.additionalInformation' })}
          >
            <Controller
              name="identCards"
              control={control}
              rules={{ required: errors.identCards ? errors.identCards.message : undefined }}
              render={({ field }) => (
                <CmdChipSelect
                  {...field}
                  id="identCards"
                  name="identCards"
                  label={f({ id: 'employee.field.idCardId' })}
                  placeholder={f({ id: 'employee.field.idCard.placeholder' })}
                  searchPlaceholder={f({ id: 'form.search' })}
                  options={
                    idCardOptions && idCardOptions.length > 0
                      ? idCardOptions.map((i) => {
                          return { value: String(i.value), item: i.item };
                        })
                      : []
                  }
                  searchTooltip={f({
                    id: 'alarms.section.vehicles.vehicleId.search.tooltip',
                  })}
                  selectButtonTooltip={f({
                    id: 'alarms.section.vehicles.vehicleId.selectButton.tooltip',
                  })}
                  resetButtonTooltip={f({
                    id: 'alarms.section.vehicles.vehicleId.resetButton.tooltip',
                  })}
                  bossCheckbox={f({ id: 'alarms.modal.timeFrame.button.selectAll' })}
                  error={errors.identCards?.message}
                  value={watch('identCards')}
                  setValue={(value: string[]) => {
                    setValue('identCards', value);
                    trigger('identCards');
                    trackEvent(['employee form', 'change', 'Change identCards']);
                  }}
                  selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                  resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                  disabled={
                    watch('types') && watch('types').includes(IS_DRIVER) && watch('customerId')
                      ? undefined
                      : true
                  }
                  setTouched={() => trigger('identCards')}
                />
              )}
            />
            <Controller
              name="personalNumber"
              control={control}
              rules={{
                required: errors.personalNumber ? errors.personalNumber.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="personalNumber"
                  label={f({ id: 'employee.field.personalNumber' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('personalNumber', value);
                    trigger('personalNumber');
                  }}
                  error={errors.personalNumber?.message}
                  touched
                />
              )}
            />
            <Controller
              name="workDepartmentId"
              control={control}
              rules={{
                required: errors.workDepartmentId ? errors.workDepartmentId.message : undefined,
              }}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  name="workDepartmentId"
                  id="workDepartmentId"
                  value={watch('workDepartmentId') || ''}
                  label={f({ id: 'employee.field.workDepartmentId' })}
                  introOption={f({ id: 'employee.field.workDepartmentId.placeholder' })}
                  options={workDepartmentOptions}
                  onChangeCallback={(value: string) => {
                    setValue('workDepartmentId', value);
                    trigger('workDepartmentId');
                    trackEvent(['employee form', 'change', 'Change workDepartmentId']);
                  }}
                  disabled={!isWorkDepatmentEnabled}
                  error={errors.workDepartmentId?.message}
                  touched={true}
                />
              )}
            />
            <Controller
              name="costCenterId"
              control={control}
              rules={{ required: errors.costCenterId ? errors.costCenterId.message : undefined }}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  name="costCenterId"
                  id="costCenterId"
                  value={watch('costCenterId') || ''}
                  label={f({ id: 'employee.field.costCenterId' })}
                  introOption={f({ id: 'employee.field.costCenterId.placeholde' })}
                  options={costCenterOptions}
                  onChangeCallback={(value: string) => {
                    setValue('costCenterId', value);
                    trigger('costCenterId');
                    trackEvent(['employee form', 'change', 'Change costCenterId']);
                  }}
                  error={errors.costCenterId?.message}
                  touched={true}
                  disabled={!isCostCenterEnabled}
                />
              )}
            />
            <Controller
              name="workingHoursFrom"
              control={control}
              rules={{
                required: errors.workingHoursFrom ? errors.workingHoursFrom.message : undefined,
              }}
              render={({ field }) => (
                <Calendar
                  {...field}
                  id="workingHoursFrom"
                  name="workingHoursFrom"
                  label={f({ id: 'employee.field.workingHoursFrom' })}
                  note="(hh:mm)"
                  mode="time"
                  error={errors.workingHoursFrom?.message}
                  touched={true}
                  onChangeCallback={(value: Date) => {
                    setValue('workingHoursFrom', value);
                    trigger('workingHoursFrom');
                    trackEvent(['employee form', 'change', 'Change workingHoursFrom']);
                  }}
                />
              )}
            />
            <Controller
              name="workingHoursTo"
              control={control}
              rules={{
                required: errors.workingHoursTo ? errors.workingHoursTo.message : undefined,
              }}
              render={({ field }) => (
                <Calendar
                  {...field}
                  id="workingHoursTo"
                  name="workingHoursTo"
                  label={f({ id: 'employee.field.workingHoursTo' })}
                  note="(hh:mm)"
                  mode="time"
                  error={errors.workingHoursTo?.message}
                  touched={true}
                  onChangeCallback={(value: Date) => {
                    setValue('workingHoursTo', value);
                    trigger('workingHoursTo');
                    trackEvent(['employee form', 'change', 'Change workingHoursTo']);
                  }}
                />
              )}
            />
          </CmdBox>
        </div>
        <div className="col-xxl-6">
          <CmdBox
            id="employee-contact-information"
            title={f({ id: 'employee.section.contactInformation' })}
          >
            <Controller
              name="email"
              control={control}
              rules={{
                required: errors.email ? errors.email.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="email"
                  label={f({ id: 'employee.field.email' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('email', value);
                    trigger('email');
                  }}
                  onBlurCallBack={(value) => {
                    if (!errors.email && !watch('username') && watch('types').includes(IS_USER)) {
                      setValue('username', value);
                      trigger('username');
                      loadRoles();
                    }
                  }}
                  required={watch('types').includes(IS_USER)}
                  error={errors.email?.message}
                  touched
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              rules={{
                required: errors.phone ? errors.phone.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="phone"
                  label={f({ id: 'employee.field.phone' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('phone', value);
                    trigger('phone');
                  }}
                  error={errors.phone?.message}
                  touched
                  required={watch('twoFactorAuth') && watch('twoFactorAuth').length > 0}
                />
              )}
            />
            <Controller
              name="addressStreet"
              control={control}
              rules={{
                required: errors.addressStreet ? errors.addressStreet.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="addressStreet"
                  label={f({ id: 'employee.field.addressStreet' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('addressStreet', value);
                    trigger('addressStreet');
                  }}
                  error={errors.addressStreet?.message}
                  touched
                />
              )}
            />
            <Controller
              name="addressCity"
              control={control}
              rules={{
                required: errors.addressCity ? errors.addressCity.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="addressCity"
                  label={f({ id: 'employee.field.addressCity' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('addressCity', value);
                    trigger('addressCity');
                  }}
                  error={errors.addressCity?.message}
                  touched
                />
              )}
            />
            <Controller
              name="addressPostal"
              control={control}
              rules={{
                required: errors.addressPostal ? errors.addressPostal.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="addressPostal"
                  label={f({ id: 'employee.field.addressPostal' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('addressPostal', value);
                    trigger('addressPostal');
                  }}
                  error={errors.addressPostal?.message}
                  touched
                />
              )}
            />
          </CmdBox>
          <CmdBox id="employee-user-data" title={f({ id: 'employee.section.userData' })}>
            <Controller
              name="username"
              control={control}
              rules={{
                required: errors.username ? errors.username.message : undefined,
              }}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="username"
                  label={f({ id: 'table_list.columns.gdprUsers.username' })}
                  type="text"
                  onChangeCallback={(value) => {
                    setValue('username', value);
                    trigger('username');
                    handleDebouncedUsernameChange(value);
                    clearErrors('username');
                  }}
                  // onBlurCallBack={(value) => {
                  //   setLastUsername(value);
                  // }}
                  required={watch('types').includes(IS_USER)}
                  error={errors.username?.message}
                  touched
                  disabled={watch('types') && watch('types').includes(IS_USER) ? undefined : true}
                />
              )}
            />
            {/* Roles Field */}
            {props.editId && userData && userData?.roles?.salesChannel?.length > 0 ? (
              <CmdLabel
                title={f({ id: 'employee.field.roles' })}
                value={getLabelFromOptions(watch('roles') || [], rolesOptions)}
              />
            ) : (
              <Controller
                name="roles"
                control={control}
                rules={{ required: errors.roles ? errors.roles.message : undefined }}
                render={({ field }) => (
                  <CmdChipSelect
                    {...field}
                    id="roles"
                    name="roles"
                    label={f({ id: 'employee.field.roles' })}
                    placeholder={f({ id: 'employee.field.idCard.placeholder' })}
                    searchPlaceholder={f({ id: 'form.search' })}
                    options={rolesOptions}
                    searchTooltip={f({ id: 'alarms.section.vehicles.vehicleId.search.tooltip' })}
                    selectButtonTooltip={f({
                      id: 'alarms.section.vehicles.vehicleId.selectButton.tooltip',
                    })}
                    resetButtonTooltip={f({
                      id: 'alarms.section.vehicles.vehicleId.resetButton.tooltip',
                    })}
                    bossCheckbox={f({ id: 'alarms.modal.timeFrame.button.selectAll' })}
                    error={errors.roles?.message}
                    value={watch('roles')}
                    setValue={(value: string[]) => {
                      setValue('roles', value);
                      trigger();
                      trackEvent(['employee form', 'change', 'Change roles']);
                    }}
                    selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                    resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                    disabled={!(watch('types') && watch('types').includes(IS_USER))}
                    setTouched={() => trigger('roles')}
                    required={
                      watch('types').includes(IS_USER) && (watch('roleIsRequired') || !props.editId)
                    }
                    touched
                  />
                )}
              />
            )}
            {renderSelectOrLabel(
              !!props.editId, // isEditMode
              Array.isArray(userData?.roles?.salesChannel) &&
                userData.roles.salesChannel.length > 0, // salesChannelHasItems
              f({ id: 'employee.field.vehicles' }), // labelTitle
              vehiclesOptions, // selectOptions
              watch('vehicles') || [], // selectValue
              f({ id: 'enum.alarms.rideOwnership.any' }), // allLabelText
              isVehicleAllowed(watch('roles'), rolesOptions), // isAllowed
              vehiclesOptions.length > 0, // optionsExist
              'vehicles', // controlName
              watch('types').includes(IS_USER) && watch('roleIsRequired'), // isRequired
              errors.vehicles // error
            )}
            {renderSelectOrLabel(
              !!props.editId, // isEditMode
              Array.isArray(userData?.roles?.salesChannel) &&
                userData.roles.salesChannel.length > 0, // salesChannelHasItems
              f({ id: 'employee.field.drivers' }), // labelTitle
              driversOptions, // selectOptions
              watch('drivers') || [], // selectValue
              f({ id: 'employees.drivers.all' }), // allLabelText
              isDriverAllowed(watch('roles'), rolesOptions), // isAllowed
              driversOptions.length > 0, // optionsExist
              'drivers', // controlName
              watch('types').includes(IS_USER) && watch('roleIsRequired'), // isRequired
              errors.drivers // error
            )}
            <Controller
              name="twoFactorAuth"
              control={control}
              rules={{
                required: errors.twoFactorAuth ? errors.twoFactorAuth.message : undefined,
              }}
              render={({ field }) => (
                <CmdCheckboxGroup
                  {...field}
                  id="twoFactorAuth"
                  name="twoFactorAuth"
                  label={f({ id: 'employee.field.twoFactorAuth' })}
                  options={[
                    {
                      value: 'twoFactorAuthWeb',
                      item: 'web',
                    },
                    {
                      value: 'twoFactorAuthMobile',
                      item: f({ id: 'employee.field.twoFactorAuthMobile' }),
                    },
                  ]}
                  tooltip={getTwoFactorAuthTooltip()}
                  setValue={(value) => {
                    setValue('twoFactorAuth', value);
                    trigger('twoFactorAuth');
                    trackEvent(['employee form', 'change', 'Change twoFactorAuth']);
                  }}
                  disabled={
                    watch('types') && watch('types').includes(IS_USER) && twoFactorAuthEnabled
                      ? undefined
                      : true
                  }
                />
              )}
            />
          </CmdBox>
        </div>
      </div>
      {isCancelButtonClicked && (
        <ConfirmDialog
          title={f({ id: 'employee.confirm.dialog.subpage.close.title' })}
          cancelCallback={handleCancelConfirmDialog}
          continueCallback={handleConfirmCloseSubpage}
          cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
          continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
        >
          {f({ id: 'employee.confirm.dialog.subpage.close.message' })}
        </ConfirmDialog>
      )}
      {userExists && (
        <ConfirmDialog
          title={f({ id: 'employee.confirm.dialog.userExist.close.title' })}
          cancelCallback={handleCancelUserExistConfirmDialog}
          continueCallback={handleSubmitlUserExist}
          cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
          continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
        >
          {props.editId
            ? f({ id: 'employee.confirm.dialog.userExist.close.message.edit' })
            : f({ id: 'employee.confirm.dialog.userExist.close.message' })}
        </ConfirmDialog>
      )}
    </CmdSubpage>
  );
}

export default Form;
